import React, { useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar, Nav, Container, NavDropdown } from "react-bootstrap";
import supabase from "../supabase";

// This style block hides the dropdown arrow and removes underlines
const NavStyles = () => (
  <style>
    {`
      #profile-nav-dropdown::after {
        display: none !important;
      }
      #profile-nav-dropdown, 
      #profile-nav-dropdown:hover, 
      #profile-nav-dropdown:focus {
        text-decoration: none !important;
        border: none !important;
        box-shadow: none !important;
      }
      .dropdown-item:hover, .dropdown-item:focus {
        text-decoration: none !important;
        background-color: #f3f4f6 !important;
      }
    `}
  </style>
);

const NavigationBar = ({ profile, onGuardNavigate }) => {
  const { companyId } = useParams();
  const navigate = useNavigate();

  // ✅ SAFETY: Wait for profile load
  if (!profile) return null;

  const displayName = profile?.name || profile?.email || "User";
  const avatarUrl = profile?.avatar_url;

  // ✅ ROBUST ROLE CHECK:
  // It checks for the original role OR the 'isTrueSuperAdmin' flag
  // set by your override interceptor in OrganizationPortal.js
  const isSuperAdmin = useMemo(() => {
    return (
      profile?.role?.toLowerCase() === "superadmin" ||
      profile?.isTrueSuperAdmin === true
    );
  }, [profile]);

  const handleLogout = async () => {
    await onGuardNavigate?.();
    try {
      await supabase.auth.signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <Navbar
      expand="lg"
      className="custom-navbar"
      style={{
        background: "#fff",
        borderBottom: "1px solid #e5e7eb",
        padding: "10px 30px",
      }}
    >
      <NavStyles />
      <Container fluid>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />

        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto" style={{ alignItems: "center" }}>
            <NavDropdown
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    textDecoration: "none",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: "#111827",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "16px",
                      overflow: "hidden",
                      border: "2px solid #e5e7eb",
                      flexShrink: 0,
                    }}
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt="Profile"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      (profile?.name || "U").trim().charAt(0).toUpperCase()
                    )}
                  </div>
                  <span
                    style={{
                      fontWeight: 700,
                      color: "#111827",
                      fontSize: "14px",
                      textDecoration: "none",
                    }}
                  >
                    {displayName}
                  </span>
                </div>
              }
              id="profile-nav-dropdown"
              align="end"
            >
              {isSuperAdmin && (
                <>
                  <NavDropdown.Item
                    style={{
                      textDecoration: "none",
                      color: "#111827",
                      cursor: "pointer",
                    }}
                    onClick={async (e) => {
                      e.preventDefault();
                      await onGuardNavigate?.();
                      localStorage.removeItem("superAdminOrgOverride");
                      // Pass the profile state forward so the dashboard knows who you are
                      navigate("/super-admin-dashboard", {
                        state: { profile },
                      });
                    }}
                  >
                    Super Admin Dashboard
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                </>
              )}

              <NavDropdown.Item
                as={Link}
                to={`/organization-portal/${
                  companyId || profile?.company_id
                }/edit-profile`}
                onClick={async (e) => {
                  e.preventDefault();
                  await onGuardNavigate?.();
                  navigate(
                    `/organization-portal/${
                      companyId || profile?.company_id
                    }/edit-profile`
                  );
                }}
                style={{ textDecoration: "none", color: "#111827" }}
              >
                Edit Profile
              </NavDropdown.Item>

              <NavDropdown.Divider />
              <NavDropdown.Item
                onClick={handleLogout}
                style={{
                  color: "#111827",
                  textDecoration: "none",
                }}
              >
                Logout
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavigationBar;
